import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ModalBase } from 'morgana';
import { PracticePayPaymentService } from '@core/practice-pay-payment/practice-pay-payment.service';
import { PayerType, PaymentTransactionType } from '@gandalf/constants';
import { PracticePayCreatePaymentTransactionRequest } from '@gandalf/model/practice-pay-create-payment-transaction-request';
import { PracticePayInitializePaymentSessionRequest } from '@gandalf/model/practice-pay-initialize-payment-session-request';
import { PracticePayPaymentTransactionResponse } from '@gandalf/model/practice-pay-payment-transaction-response';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';

export interface PracticePayPaymentModalData {
	practiceLocationId: number;
	amount: number;
	entityId: number;
	payerType: PayerType;
}

export interface PracticePayPaymentModalResult {
	manualProcessRequested: boolean;
	transaction?: PracticePayPaymentTransactionResponse;
	cardLast4?: string;
	cardBrand?: string;
}

@Component({
	selector: 'pms-practice-pay-payment-modal',
	templateUrl: './practice-pay-payment-modal.component.html',
	styleUrls: ['./practice-pay-payment-modal.component.scss'],
})
export class PracticePayPaymentModalComponent extends ModalBase<PracticePayPaymentModalData, PracticePayPaymentModalResult> implements OnInit {

	@ViewChild('modal')
	modal: DialogComponent;

	iframeUrl: SafeResourceUrl;
	isLoading = true;
	isProcessing = false;
	errors: string[] = [];

	private transactionIdentifier: string;
	private transactionStatus: string;
	private cardLast4: string;
	private cardBrand: string;

	constructor(
		private practicePayPaymentService: PracticePayPaymentService,
		private sanitizer: DomSanitizer,
	) {
		super();
	}

	ngOnInit(): void {
		this.initializePaymentSession();
	}

	initializePaymentSession() {
		this.isLoading = true;
		this.errors = [];
		const request = new PracticePayInitializePaymentSessionRequest();
		request.practiceLocationId = this.data.practiceLocationId;
		request.amount = this.data.amount;
		request.entityId = this.data.entityId;
		request.payerType = this.data.payerType;
		this.practicePayPaymentService.initializePaymentSession(request)
			.subscribe({
				next: (url: string) => {
					this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
					this.isLoading = false;
				},
				error: (error: HttpErrorResponse) => {
					this.isLoading = false;
					const errorMessage = error?.error?.message
						|| 'Failed to initialize payment session. Please try again.';
					this.errors = [errorMessage];
				},
			});
	}

	@HostListener('window:message', ['$event'])
	onMessage(event: MessageEvent) {
		const data = event.data;

		if (data?.type === 'PAYMENT_COMPLETED' && data?.result) {
			const paymentResult = data.result;
			this.transactionIdentifier = paymentResult.transactionId || '';
			this.transactionStatus = paymentResult.status || '';
			this.cardLast4 = paymentResult.paymentMethod?.card?.last4 || '';
			this.cardBrand = paymentResult.paymentMethod?.card?.brand || '';
			this.createPaymentTransaction(paymentResult);
		}
	}

	private createPaymentTransaction(paymentResult: any) {
		this.isProcessing = true;
		const request = new PracticePayCreatePaymentTransactionRequest();
		request.practiceLocationId = this.data.practiceLocationId;
		request.transactionIdentifier = this.transactionIdentifier;
		request.status = this.transactionStatus;
		request.transactionModality = this.practicePayPaymentService.getTransactionModalityFromCardBrand(this.cardBrand);
		request.transactionType = PaymentTransactionType.SALE;
		request.errorCode = paymentResult.refusalReasonCode || null;
		request.errorMessage = paymentResult.refusalReason || null;

		this.practicePayPaymentService.createPaymentTransaction(request)
			.subscribe({
				next: (response: PracticePayPaymentTransactionResponse) => {
					this.isProcessing = false;
					if (response.isTransactionApproved) {
						this.closeModal({
							manualProcessRequested: false,
							transaction: response,
							cardLast4: this.cardLast4,
							cardBrand: this.cardBrand,
						});
					} else {
						this.errors = ['Payment was declined. Please try again.'];
					}
				},
				error: () => {
					this.isProcessing = false;
					this.errors = ['Failed to process payment. Please try again.'];
				},
			});
	}

	onIframeLoad() {
		this.isLoading = false;
	}

	applyPaymentWithoutProcessingCard() {
		this.closeModal({
			manualProcessRequested: true,
		});
	}

	/* istanbul ignore next: closeModal */
	closeModal(result?: PracticePayPaymentModalResult) {
		this.dynamicModalRef.close(this.modal, result);
	}
}
